import React from 'react';

import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { Alert, alertClasses } from '@mui/material';

import type { Nullable } from '@clearverify/types';

import { ActiveInactive } from 'src/api';
import { Button } from 'src/components/common';
import { ModalId } from 'src/constants/modals';
import { useConsumerById } from 'src/hooks/consumer';
import { useModal } from 'src/hooks/shared';
import { useCurrentUserVerifier } from 'src/hooks/verifier';
import type { SxProp } from 'src/types/mui';

type NewRequestBlockProps = {
  employeeId: Nullable<string>;
  disabled?: boolean;
};

export const NewRequestBlock: React.FC<NewRequestBlockProps> = ({ disabled, employeeId }) => {
  const { isCommercialVerifier } = useCurrentUserVerifier();

  const { openModal, closeModal } = useModal();

  const clickHandler = React.useCallback(() => {
    const newModalId = isCommercialVerifier
      ? ModalId.VerificationRequestCreateByCommercialVerifier
      : ModalId.VerificationRequestCreateByGovernmentVerifier;

    openModal(newModalId);
    closeModal(ModalId.ReverificationSubmitByVerifierStaff);
  }, [closeModal, isCommercialVerifier, openModal]);

  const { consumer } = useConsumerById(employeeId);

  if (consumer?.freeze === ActiveInactive.Active) return null;

  return (
    <Alert
      variant="outlined"
      icon={<InfoIcon fontSize="inherit" />}
      sx={alertStyles}
      action={
        <Button color="inherit" size="small" onClick={clickHandler} disabled={disabled}>
          Create
        </Button>
      }
    >
      If you want to change settings create a New Request
    </Alert>
  );
};

const alertStyles: SxProp = theme => ({
  mt: 3,
  color: theme.palette.info.dark,
  borderColor: theme.palette.info.main,
  [`& .${alertClasses.icon}`]: {
    color: theme.palette.info.dark,
  },
  [`& .${alertClasses.action}`]: {
    alignItems: 'center',
    padding: 0,
  },
});
